import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { getLocale, useIntl } from 'umi';
import moment from 'moment';
import {
  Card,
  Segment,
  Bubble,
  Justify,
  Col,
  Row,
  Select,
} from 'tea-component';

import SvgIcon from '@/pages/home/components/SvgIcon';
import { useLinkPath, useLocal } from '@/pages/dashboard/utils/customHook';
import useItem from './useItem';
import useOverviewLocalesIntl from '../../_config/useOverviewLocalesIntl';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import {
  addChartLabel,
  changeMin,
  compareTrend,
  xAxisHighLight,
} from '@/pages/dashboard/components/Chart/_config/plug';
import {
  formatNum,
  formatTruNum,
  unitConversion,
  getIsErrorValue,
  unitConversionNew,
  getDateFormatString,
  formatSmallValue,
  formatSmallPercent,
  jumpDetail,
} from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import EventDetailsPop, { useEventDetailsPop } from '../../../eventAnalysis/EventDetailsPop';

import '@/pages/dashboard/assets/scss/page/Overlay/components/tableCard.scss';
import { hasPercentList } from './_config';

import './index.scss';
import ACard from '@/pages/dashboard/components/ACard';

export default function CardLine(props: any) {
  const { getLocalText } = useLocal();
  const { formatMessage } = useIntl();
  const {
    tabList,
    type,
    renderIndex,
    gameCode,
    formValue,
    separatedOption,
    error,
    eventData,
    placeholderId = '',
  } = props;

  const popRef = useRef();
  const lineChartRef = useRef();
  const barChartRef = useRef();
  const lineChartObj = lineChartRef?.current?.instance();
  const resizeNum = useRef(0);// 换行的处理影响到了echart的自适应，echart没有实时更新
  // 事件详情数据
  const { currentEvents, slotConfigFunc } = useEventDetailsPop(
    eventData,
    popRef,
  );
  const linkPath = useLinkPath('eventAnalysis/eventDetails');

  const eventSlotConfigFunc = useCallback(
    (opt, chart) => {
      const options = slotConfigFunc(opt, chart);

      options.yAxis = [
        options.yAxis,
        {
          show: false,
          axisLabel: {
            formatter () {
              return "";
            }
          }
        },
      ];
      opt.grid.x2 = 2;
      options.series[options.series.length - 1].yAxisIndex = 1;

      return options;
    },
    [slotConfigFunc],
  );

  // 给segment项加上指标解释： 日 WD2 WD3 WD7  周 WW2 WW3  月 WM2 WM3
  for (const iterator of tabList) {
    if (iterator?.bubble) {
      iterator.text = (
        <Bubble className="segment-bubble" dark content={iterator?.bubble}>
          {iterator?.text}
        </Bubble>
      );
      delete iterator.bubble;
    }
  }

  const newprops = useItem(props);
  const {
    data,
    loading,
    dataType,
    selected,
    dataUnit,
    userQuota,
    retentionMetricsList,
    getWeekDayStr,
    downloadData,
    onDataTypeChange,
  } = newprops;

  const [, setSelectText] = useState(
    formatMessage({ id: 'common_Accumulated' }),
  );
  const [selectedValue, setSelectedValue] = useState(selected);
  const [showChangeTag, setShowChangeTag] = useState(false);
  const [, setMinMax] = useState({ min: 0, max: 0 });
  const { CHART_TYPE, DATE_DIMENSION } = useOverviewLocalesIntl();

  const [dheight, setdheight] = useState(300);
  // const [isWrap, setWrap] = useState(true);
  const rightIcon = [
    {
      type: 'column',
      icon: 'column-chart',
      Bubble: formatMessage({ id: 'common_bar_chart' }),
    },
    {
      type: 'turn',
      icon: 'left-turn-right',
      Bubble: formatMessage({
        id: showChangeTag
          ? 'change_tag_bubble_show'
          : 'change_tag_bubble_hidden',
      }),
    },
    {
      type: 'discount',
      icon: 'discount-chart',
      Bubble: formatMessage({ id: 'common_line_chart' }),
    },
    {
      type: 'sheet',
      icon: 'sheet',
      Bubble: formatMessage({ id: 'common_table' }),
    },
    {
      type: 'vs',
      icon: 'vs-icon',
      Bubble: formatMessage({ id: 'common_comparison_analysis' }),
    },
  ];

  const [comType, setChartComType] = useState(rightIcon[2].type);
  const [iseye, setIseye] = useState(true);

  const retentionNameList = [
    getLocalText('overview_day_Weighted_2'),
    getLocalText('overview_day_Weighted_3'),
    getLocalText('overview_day_Weighted_7'),
    getLocalText('overview_week_Weighted_2'),
    getLocalText('overview_week_Weighted_3'),
    getLocalText('overview_month_Weighted_2'),
    getLocalText('overview_month_Weighted_3'),

    getLocalText('overview_month_retention_3'),
    getLocalText('overview_month_retention_2'),
    getLocalText('overview_weekly_W2'),
    getLocalText('overview_weekly_W3'),
    getLocalText('overview_daily_D2'),
    getLocalText('overview_daily_D3'),
    getLocalText('overview_Payer%'),
  ];

  // 数值需要百分比展示
  const percentNameList = [
    'Payer% Today',
    'Payer% Last Week',
    'Payer% This Week',
    'Payer% Last Month',
    'Payer% Last Year',
    'Payer% This Year',
    ...retentionNameList,
  ];

  useEffect(() => {
    if (dataType === 'arpu') {
      const dataList: any = [];
      data?.[CHART_TYPE.COLUMN]?.series?.map((item: any) =>
        dataList.push(...item?.data),
      );
      const max = Math.max(...dataList);
      const min = Math.min(...dataList);
      setMinMax({ min, max });
    }
  }, [dataType]);


  const formatterFunc = (param: any, dataUnit?: any) => {
    const isTwoDataCompare = param.length <= 2; // 两者对比（3是因为有1个空value）
    const isSplit = (!percentNameList.includes(param[0].seriesName));

    // param?.pop(); // 删除事件详情数量的折线数据, 不需要展示在浮窗中
    const splitData = param?.map((item: any) => {
      const ecahrtValue =
        dataType === 'arpu'
          ? formatSmallValue(item?.value, 'decimals', 2)
          : (percentNameList.includes(item.seriesName) ||
            hasPercentList?.includes(dataType))
            ? formatSmallPercent(item?.value, 2)
            : formatNum(item.value, false, userQuota.includes(dataType));

      const isErrorValue = getIsErrorValue(item.value);

      const isToday = item.seriesName.includes(formatMessage({ id: 'overview_daily_Today' })) || item.seriesName.includes(formatMessage({ id: 'overview_This_Week' })) || item.seriesName.includes(formatMessage({ id: 'overview_This_Month' }));
      const isSDLW = item.seriesName.includes(formatMessage({ id: 'overview_daily_Last_Week' }));
      const isSDLM = item.seriesName.includes(formatMessage({ id: 'overview_daily_Last_Month' }));
      const isSDLY = item.seriesName.includes(formatMessage({ id: 'overview_daily_Last_Year' }));

      const beforeTimeMap: any = {
        day: moment(item.axisValue).subtract(1, 'week').format('YYYY-MM-DD'),
        week: (() => {
          // 获取当前日期
          const currentDate = moment(item.axisValue);
          // 获取当前日期减去1个月的目标日期
          const targetDate = currentDate.clone().subtract(1, 'month');
          // 如果目标日期的日期部分和当前日期的日期部分相同，则返回目标日期的格式化结果，否则返回 "-"
          return currentDate.date() === targetDate.date() ? targetDate.format('YYYY-MM-DD') : "-";
        })(),
        month: (() => {
          const currentDate = moment(item.axisValue);
          const targetDate = currentDate.clone().subtract(1, 'year');
          return currentDate.date() === targetDate.date() ? targetDate.format(type === 'DAY' ? 'YYYY-MM-DD' : 'YYYY-MM') : "-";
        })(),
      };

      const now = getWeekDayStr(item.axisValue, type);
      const lastWeek = getWeekDayStr(beforeTimeMap.day, type);
      const lastMonth = getWeekDayStr(beforeTimeMap.week, type);
      const lastYear = getWeekDayStr(beforeTimeMap.month, type);
      const before = getWeekDayStr(
        beforeTimeMap[type?.toLocaleLowerCase()],
        type,
      );
      const title =
        isToday ? now :
          isSDLW ? lastWeek :
            isSDLM ? lastMonth :
              isSDLY ? lastYear : before;

      const metricsNum = isErrorValue
        ? '-'
        : (dataUnit && ecahrtValue !== '-')
          ? dataUnit + ecahrtValue
          : ecahrtValue;

      const dataEntry = {
        color: item.color,
        name: item.seriesName,
        metricsNum,
      };

      const data = [dataEntry];

      const result = {
        title,
        data,
      };

      return result;
    });

    const title =
      type === 'MONTH' ? param[0].axisValue : getWeekDayStr(param?.[0]?.axisValue);
    const data = param?.map((item) => ({
      name: item.seriesName,
      color: item.color,
      metricsNum: formatSmallPercent(item?.value, 2),
    }));

    return getEchartTooltipText({
      title,
      data,
      isSplit,
      isTwoDataCompare,
      splitData,
    });
  };

  // 分维度 柱状图和折线图浮窗展示
  const dimensionFormatterFunc = (param) => {
    // param?.pop(); // 删除事件详情数量的折线数据, 不需要展示在浮窗中
    const metrics = tabList.find((tab) => tab.value === dataType);
    const metricsName = metrics?.name || metrics?.text;
    const title = type === 'MONTH' ? param[0].axisValue : getWeekDayStr(param?.[0]?.axisValue);

    return getEchartTooltipText({
      title,
      metricsName,
      isSort: true,
      data: param.map(({ seriesName, value, color }) => ({
        name: seriesName,
        metricsNum:
          percentNameList.includes(metricsName) ||
            hasPercentList?.includes(dataType)
            ? formatSmallPercent(value, 2)
            : formatNum(value, false, userQuota.includes(dataType)),
        color,
      })),
    });
  };

  // 是否有数据
  const hasDataFlag = useMemo(() => {
    const arr = data?.line?.series || [];
    let flag = false;
    for (let index = 0;index < arr.length;index++) {
      if (arr[index]?.data?.findIndex((item) => item) !== -1) {
        flag = true;
        break;
      }
    }
    return flag;
  }, [data]);

  useEffect(() => {
    if (selectedValue === 'accumulated') {
      setSelectText(formatMessage({ id: 'common_Accumulated' }));
    } else {
      const selectItem: any = separatedOption?.find(
        (item: any) => item?.value === selectedValue,
      );
      setSelectText(selectItem?.text);
    }
  }, [getLocale()]);

  // icon点击跳转
  const iconClickfunc = (e: any) => {
    if (e.type === 'turn') {
      setShowChangeTag(!showChangeTag);
      return;
    }
    if (e.type === 'vs') {
      const indicatorsNameMap: any = {
        d2: 'd237',
        weighted_d2: 'wd237',
        weighted_d3: 'weightedD3',
        weighted_d7: 'weightedD7',
        w2: 'w23',
        weighted_w2: 'ww23',
        weighted_w3: 'weightedW3',
        m2: 'm23',
        weighted_m2: 'wm23',
        weighted_m3: 'weightedM3',
      };
      const indicators = indicatorsNameMap[dataType] ?? dataType;
      jumpDetail(`/dashboard/game/${gameCode}/comparisonAnalysis/dateComparison`, formValue, indicators);
      return;
    }

    e.type !== 'turn' && e.type !== 'vs' && setChartComType(e.type);
  };
  // line图表配置
  const lineChartOtherConfig = (opt: any) => {
    if (!retentionMetricsList.includes(dataType)) {
      opt.legend.formatter = (name) => name?.replace(/[SDLW|SDLM|SDLY|SWLM|SMLY]+/g, (matchStr) => {
        const map = {
          SDLW: 'Same Day Last Week',
          SDLM: 'Same Day Last Month',
          SDLY: 'Same Day Last Year',
          SWLM: 'Same Week Last Month',
          SMLY: 'Same Month Last Year',
        };
        return map[matchStr] || matchStr;
      });
    }
    opt.xAxis.boundaryGap = false;
    opt.xAxis.data = data?.[CHART_TYPE.LINE]?.xAxis ?? [];
    // opt.series = testMinValue(opt,0.1);
    opt.xAxis[0].axisLabel.formatter = function (value) {
      const valueArr = value?.split('-');
      if (type === DATE_DIMENSION.MONTH) {
        return `${valueArr?.[0]}-${valueArr?.[1]}`;
      } 
      return `${valueArr?.[1]}-${valueArr?.[2]}`;
      
    };
    Object.keys(data?.[CHART_TYPE.LINE]?.yAxis ?? {})?.forEach((key) => {
      if (typeof opt.yAxis[key] === 'object') {
        opt.yAxis[key] = {
          ...opt.yAxis[key],
          ...data?.[CHART_TYPE.LINE]?.yAxis[key],
        };
      } else {
        opt.yAxis[key] = data?.[CHART_TYPE.LINE]?.yAxis[key];
      }
    });
    if (selectedValue === 'accumulated') opt = compareTrend(opt);
    opt = addChartLabel(opt, {
      formatter: (params) => {
        if (dataType === 'arpu') return formatSmallValue(params?.value, 'decimals', 2);
        const result =
          hasPercentList?.includes(dataType)
            ? formatSmallPercent(params?.value, 2)
            : formatNum(params?.value, false, userQuota.includes(dataType));
        if (!hasPercentList?.includes(dataType) && result?.length >= 9) {
          return unitConversionNew(params?.value, {
            isFloat: true,
            fixedNum: 3,
          });
        }
        return result;
      },
      show: iseye && selectedValue === 'accumulated',
      formatNum: (value) => {
        if (dataType === 'arpu') return formatSmallValue(value, 'decimals', 2);
        if (hasPercentList?.includes(dataType)) {
          return formatSmallPercent(value, 2);
        } 
        return formatNum(value, false, userQuota.includes(dataType));
        
      },
    });
    opt = changeMin(opt, lineChartObj, {
      show: showChangeTag,
    });
    opt = xAxisHighLight(opt, {
      formatter: (value) => {
        const valueArr = value?.value?.split('-');
        if (type === DATE_DIMENSION.MONTH) {
          return `${valueArr?.[0]}-${valueArr?.[1]}`;
        } 
        return `${valueArr?.[1]}-${valueArr?.[2]}`;
        
      },
    });

    opt.yAxis.axisLabel.formatter = (value) => {
      const formatLabel = value ? unitConversion(value, true) : value;
      let num;
      num =
        value < 1 && value > 0
          ? dataType === 'arpu'
            ? formatSmallValue(value, 'decimals', 2)
            : parseFloat(value.toFixed(3))
          : formatLabel;
      if (hasPercentList?.includes(dataType)) {
        if (value === 0) {
          num = '0%';
        }
        num = formatSmallPercent(value, 2, true);
      }
      if (showChangeTag && dataType === 'tru') {
        num = formatTruNum(value, true, 3);
      }

      return num;
    };

    // 事件会影响间距，有事件的时候间距太小（高亮时，横坐标也覆盖不全）
    opt.xAxis[0].axisLabel.margin = 16;
    opt.tooltip.axisPointer.label.margin = 11;
    // opt = formatyAxisSmallerThanOne(opt,hasPercentList?.includes(dataType))
    // opt = eventSlotConfigFunc(opt,lineChartObj)
    return opt;
  };
  // 柱状图图表配置
  const columunChartOtherConfig = (opt: any) => {
    // opt.series = testMinValue(opt,0.1);
    opt.xAxis.data = data?.[CHART_TYPE.COLUMN]?.xAxis ?? [];
    opt.xAxis[0].axisLabel.formatter = function (value) {
      const valueArr = getDateFormatString(type, value).split('-');
      if (type === DATE_DIMENSION.MONTH) {
        return `${valueArr?.[0]}-${valueArr?.[1]}`;
      } 
      return `${valueArr?.[1]}-${valueArr?.[2]}`;
      
    };
    if (!retentionMetricsList.includes(dataType)) {
      opt.legend.formatter = (name) => name?.replace(/[SDLW|SDLM|SDLY|SWLM|SMLY]+/g, (matchStr) => {
        const map = {
          SDLW: 'Same Day Last Week',
          SDLM: 'Same Day Last Month',
          SDLY: 'Same Day Last Year',
          SWLM: 'Same Week Last Month',
          SMLY: 'Same Month Last Year',
        };
        return map[matchStr] || matchStr;
      });
    }
    Object.keys(data?.[CHART_TYPE.COLUMN]?.yAxis ?? {})?.forEach((key) => {
      if (typeof opt.yAxis[key] === 'object') {
        opt.yAxis[key] = {
          ...opt.yAxis[key],
          ...data?.[CHART_TYPE.LINE]?.yAxis[key],
        };
      } else {
        opt.yAxis[key] = data?.[CHART_TYPE.LINE]?.yAxis[key];
      }
    });
    opt = addChartLabel(opt, {
      formatter: (params) => {
        if (dataType === 'arpu') return formatSmallValue(params?.value, 'decimals', 2);
        const result =
          hasPercentList?.includes(dataType) || dataType === 'payer_ratio'
            ? formatSmallPercent(params?.value, 2)
            : formatNum(params?.value, false, userQuota.includes(dataType));
        if (!hasPercentList?.includes(dataType) && result?.length >= 9) {
          return unitConversionNew(params?.value, {
            isFloat: true,
            fixedNum: 3,
          });
        }
        return result;
      },
      show: iseye && selectedValue === 'accumulated',
      formatNum: (value) => {
        if (dataType === 'arpu') return formatSmallValue(value, 'decimals', 2);
        if (hasPercentList?.includes(dataType) || dataType === 'payer_ratio') {
          return formatSmallPercent(value, 2);
        } 
        return formatNum(value, false, userQuota.includes(dataType));
        
      },
    });
    opt = xAxisHighLight(opt, {
      formatter: (value) => {
        const valueArr = getDateFormatString(type, value?.value).split('-');
        if (type === DATE_DIMENSION.MONTH) {
          return `${valueArr?.[0]}-${valueArr?.[1]}`;
        } 
        return `${valueArr?.[1]}-${valueArr?.[2]}`;
        
      },
    });
    opt.yAxis.axisLabel.formatter = (value) => {
      const formatLabel = value ? unitConversion(value, true) : value;
      let num;
      num =
        value < 1 && value > 0
          ? dataType === 'arpu'
            ? formatSmallValue(value, 'decimals', 2)
            : parseFloat(value.toFixed(3))
          : formatLabel;
      if (hasPercentList?.includes(dataType)) {
        if (value === 0) {
          num = '0%';
        }
        num = formatSmallPercent(value, 2, true);
      }
      if (showChangeTag && dataType === 'tru') {
        num = formatTruNum(value, true, 3);
      }
      return num;
    };
    // opt = formatyAxisSmallerThanOne(opt,hasPercentList?.includes(dataType))
    // 事件会影响间距，有事件的时候间距太小（高亮时，横坐标也覆盖不全）
    opt.xAxis[0].axisLabel.margin = 16;
    opt.tooltip.axisPointer.label.margin = 11;
    return opt;
  };

  const htmlHeight = document.getElementsByClassName(`daily-segment-${renderIndex}`)?.[0]?.clientHeight || 0;
  useEffect(() => {
    setHeightFunc();
  }, [htmlHeight]);

  useEffect(() => {
    window.addEventListener('resize', setHeightFunc);
    return () => {
      window.removeEventListener('resize', setHeightFunc);
    };
  }, []);

  const setHeightFunc = () => {
    resizeNum.current = resizeNum.current + 1;
    const htmlHeight = document.getElementsByClassName(`daily-segment-${renderIndex}`)?.[0]?.clientHeight || 0;
    const boxHeight = document.getElementsByClassName(`dbd-daily-chart-card-body-${renderIndex}`)?.[0]?.clientHeight || 0;
    // 40为padding、36为筛选器一行的高度
    setdheight(boxHeight - 40 - htmlHeight - 36);
  };

  return (
    <Card className="overlay-line overlay-line-daily">
      <Card.Body className={`dbd-daily-chart-card-body-${renderIndex}`}>
        <Segment
          className={`overlay-line-tab dbo-overlay-style daily-segment-${renderIndex}`}
          value={dataType}
          onChange={(value) => {
            onDataTypeChange(
              value,
              tabList.find((tab) => tab.value === value)?.text,
              selectedValue,
            );
          }}
          options={tabList}
        />
        <Justify
          left={
            <>
              <Select
                listHeight={226}
                appearance="default"
                className="overlay-line-select-text"
                options={separatedOption}
                value={selectedValue}
                onChange={(value, e) => {
                  setSelectText(e.option.text);
                  setSelectedValue(value);
                  onDataTypeChange(
                    dataType,
                    tabList.find((tab) => tab.value === dataType)?.text,
                    value,
                  );
                }}
                placeholder="accumulated"
              />
            </>
          }
          right={
            hasDataFlag && (
              <div className="overlay-line-flex">
                {comType === 'sheet' ||
                  selectedValue !== 'accumulated' ? null : (
                    <Bubble
                      dark
                      placement="top"
                      content={
                        iseye
                          ? formatMessage({ id: 'common_show_values' })
                          : formatMessage({ id: 'common_hide_values' })
                      }
                    >
                      <div
                        className="overlay-line-item"
                        onClick={() => {
                          setIseye(!iseye);
                        }}
                      >
                        <SvgIcon
                          className="overlay-line-icon"
                          type={iseye ? 'open-eyes' : 'eyes-closed'}
                        />
                      </div>
                    </Bubble>
                  )}
                {rightIcon.map((e, index) => {
                  const isActive =
                    e.type == comType ||
                    (comType === 'discount' &&
                      e.type === 'turn' &&
                      showChangeTag);
                  if (comType !== 'discount' && e.type === 'turn') {
                    return;
                  }
                  return (
                    <Bubble
                      key={e.type + index}
                      dark
                      placement="top"
                      content={e.Bubble}
                    >
                      <ACard url={e.type === 'vs' ? `/dashboard/game/${gameCode}/comparisonAnalysis/dateComparison` : ''}>
                        <div
                          className="overlay-line-item"
                          onClick={() => iconClickfunc(e)}
                        >
                          <SvgIcon
                            className={
                              isActive
                                ? 'overlay-line-action'
                                : 'overlay-line-icon'
                            }
                            type={e.icon}
                          />
                        </div>
                      </ACard>
                    </Bubble>
                  );
                })}
                {!hasDataFlag ? null : (
                  <Bubble dark placement="top" content="">
                    <i onClick={downloadData}>
                      <SvgIcon className="overlay-line-down" type="download" />
                    </i>
                  </Bubble>
                )}
              </div>
            )
          }
        />
        <Row>
          {comType === 'column' ? (
            <Col>
              <div className="dbd-overlay-chart">
                <Abnormal
                  isLoading={loading}
                  isError={error}
                  isNoData={!hasDataFlag}
                  style={{ height: dheight }}
                  component={
                    <BarChart
                      key={
                        JSON.stringify(data?.[CHART_TYPE.LINE]?.series) +
                        iseye +
                        dheight + JSON.stringify(formValue) + JSON.stringify(eventData) +
                        resizeNum.current
                      }
                      ref={barChartRef}
                      height={dheight}
                      title={''}
                      legendData={data?.[CHART_TYPE.COLUMN]?.legend ?? []}
                      selectedLegendData={data?.[CHART_TYPE.COLUMN]?.selectedLegend ?? {}}
                      xAxisData={data?.[CHART_TYPE.COLUMN]?.xAxis ?? []}
                      series={data?.[CHART_TYPE.COLUMN]?.series ?? []}
                      formatterFunc={(params: any) =>
                        selectedValue === 'accumulated'
                          ? formatterFunc(params, dataUnit)
                          : dimensionFormatterFunc(params)
                      }
                      setOtherConfigFunc={(opt: any) =>
                        columunChartOtherConfig(opt)
                      }
                      chartEventConfig={eventSlotConfigFunc}
                    />
                  }
                />
                <EventDetailsPop
                  key={JSON.stringify(formValue)}
                  isOverview={true}
                  currentEvents={currentEvents}
                  pathname={linkPath}
                  timeType={formValue?.timeType}
                  popRef={popRef}
                  style={{ top: renderIndex === 0 ? '85%' : '104%' }}
                />
              </div>
            </Col>
          ) : (
            <></>
          )}
          {comType === 'discount' ? (
            <Col>
              <div className="dbd-overlay-chart">
                <Abnormal
                  isLoading={loading}
                  isError={error}
                  isNoData={!hasDataFlag}
                  style={{ height: dheight }}
                  component={
                    <>
                      <LineChart
                        height={dheight}
                        ref={lineChartRef}
                        key={
                          JSON.stringify(data?.[CHART_TYPE.LINE]?.series) +
                          iseye +
                          showChangeTag +
                          dheight + JSON.stringify(formValue) + JSON.stringify(eventData) +
                          resizeNum.current
                        }
                        title={''}
                        legendData={data?.[CHART_TYPE.LINE]?.legend ?? []}
                        selectedLegendData={data?.[CHART_TYPE.LINE]?.selectedLegend ?? {}}
                        xAxisData={data?.[CHART_TYPE.LINE]?.xAxis ?? []}
                        series={data?.[CHART_TYPE.LINE]?.series ?? []}
                        formatterFunc={(params: any) =>
                          selectedValue === 'accumulated'
                            ? formatterFunc(params, dataUnit)
                            : dimensionFormatterFunc(params)
                        }
                        setOtherConfigFunc={(opt: any) => lineChartOtherConfig(opt)}
                        chartEventConfig={eventSlotConfigFunc}
                      />
                      {!loading && placeholderId && (
                        <div
                          id={placeholderId}
                          className="dtd-chart-x-placehoder"
                        ></div>
                      )}
                    </>
                  }
                />
                {/* <EventsPopover /> */}
                <EventDetailsPop
                  key={JSON.stringify(formValue) + JSON.stringify(currentEvents)}
                  isOverview={true}
                  currentEvents={currentEvents}
                  pathname={linkPath}
                  timeType={formValue?.timeType}
                  popRef={popRef}
                  style={{ top: (renderIndex === 1 && getLocale() !== 'en-US') ? '104%' : '85%' }}
                />
              </div>
            </Col>
          ) : (
            <></>
          )}

          {comType === 'sheet' ? (
            <Col>
              <Abnormal
                isLoading={loading}
                isError={error}
                isNoData={!hasDataFlag}
                style={{ height: dheight }}
                component={
                  <MiniTable
                    loading={loading}
                    height={dheight}
                    tableOption={data?.[CHART_TYPE.TABLE]?.option}
                    tableList={data?.[CHART_TYPE.TABLE]?.data}
                    recordKey={'date'}
                  />
                }
              />
            </Col>
          ) : (
            <></>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
}
